import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from "react-router-dom";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Pie } from 'react-chartjs-2';
import useProfilePicture from "../hooks/useProfilePicture";
import flowTrackerCleanupService from "../services/flowTrackerCleanupService";
import flowTrackerService from "../services/flowTrackerService";
import firebase from "../firebaseConfig";
import toast, { toastConfig } from 'react-simple-toasts';
import 'react-simple-toasts/dist/theme/dark.css';
import DashBoardNavBar from "./DashboardNavbar";
import NoSubscription from "./NoSubscription";
import '../styles/account.css';
import { FiUser, FiCalendar, FiMoon, FiLock, FiTrash2, FiClock, FiMail, FiCamera, FiLink, FiSettings } from 'react-icons/fi';
import { Slider } from 'primereact/slider';
import "primereact/resources/themes/lara-light-indigo/theme.css";

toastConfig({ theme: 'dark' });

ChartJS.register(ArcElement, Tooltip, Legend);

const Account = ({ user }) => {
    const [userData, setUserData] = useState(null);
    const [planValidity, setPlanValidity] = useState(0);
    const [eraseDataModal, setEraseDataModal] = useState(false);
    const [nightPerson, setNightPerson] = useState(false);
    const [socialLink, setSocialLink] = useState({ type: '', url: '' });
    const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
    const [documentSizeData, setDocumentSizeData] = useState(null);
    const [isLoadingDocSize, setIsLoadingDocSize] = useState(false);
    
    // Utiliser le hook personnalisé pour gérer la photo de profil
    const { profilePicture, updateProfilePicture } = useProfilePicture(user?.uid);
    
    // États pour les paramètres du timer
    const [timerSettings, setTimerSettings] = useState({
        focus: 45,
        shortBreak: 10,
        longBreak: 45
    });
    const [isUpdatingTimerSettings, setIsUpdatingTimerSettings] = useState(false);
    const [isCleaningFlowTracker, setIsCleaningFlowTracker] = useState(false);
    const [cleanupStats, setCleanupStats] = useState(null);
    const [isMigratingFlowTracker, setIsMigratingFlowTracker] = useState(false);
    const [migrationStats, setMigrationStats] = useState(null);
    const [dataSharingConsent, setDataSharingConsent] = useState(false);
    
    // Limites pour les durées des timers
    const timerLimits = {
        focus: { min: 30, max: 120 }, // 30min - 2h
        shortBreak: { min: 5, max: 20 }, // 5-20 min
        longBreak: { min: 30, max: 90 } // 30min - 1h30
    };
    const navigate = useNavigate();

    // Fonction pour calculer la taille approximative d'un objet JavaScript en octets
    const calculateObjectSize = (obj) => {
        const objectList = [];
        const stack = [obj];
        let bytes = 0;

        // Pour éviter les références circulaires
        const detectCircular = (object) => {
            if (typeof object !== 'object' || object === null) {
                return false;
            }
            return objectList.includes(object);
        };

        while (stack.length) {
            const value = stack.pop();

            if (typeof value === 'boolean') {
                bytes += 4;
            } else if (typeof value === 'string') {
                bytes += value.length * 2;
            } else if (typeof value === 'number') {
                bytes += 8;
            } else if (typeof value === 'object' && value !== null && !detectCircular(value)) {
                objectList.push(value);

                if (Array.isArray(value)) {
                    for (let i = 0; i < value.length; i++) {
                        stack.push(value[i]);
                    }
                } else {
                    const keys = Object.keys(value);
                    for (let i = 0; i < keys.length; i++) {
                        bytes += keys[i].length * 2; // Taille de la clé
                        stack.push(value[keys[i]]);
                    }
                }
            }
        }

        return bytes;
    };

    // Fonction pour analyser la taille du document utilisateur
    const analyzeDocumentSize = (data) => {
        if (!data) return null;

        const categories = [
            { name: 'Profile Picture', key: 'profilePicture', size: 0, color: '#FF6384' },
            { name: 'Flow State Habits', key: 'FlowTrigger_3', size: 0, color: '#36A2EB' },
            { name: 'Flow Tracker', key: 'FlowTrackerData', size: 0, color: '#FFCE56' },
            { name: 'Daily Todo List', key: 'DailyTodoListData', size: 0, color: '#4BC0C0' },
            { name: 'Personal Notes', key: 'personalNotes', size: 0, color: '#9966FF' },
            { name: 'Journal Entries', key: 'journal', size: 0, color: '#FF9F40' },
            { name: 'Task Management', key: 'SingleTasking_2', size: 0, color: '#8AC926' },
            { name: 'Goals', key: 'Goals_1', size: 0, color: '#1982C4' },
            { name: 'Release', key: 'Release_5', size: 0, color: '#6A4C93' },
        ];

        // Calculer la taille de chaque catégorie
        let totalSize = 0;
        let otherSize = 0;

        // Calculer la taille de chaque catégorie connue
        categories.forEach(category => {
            if (data[category.key]) {
                category.size = calculateObjectSize(data[category.key]);
                totalSize += category.size;
            }
        });

        // Calculer la taille des autres données
        Object.keys(data).forEach(key => {
            if (!categories.some(cat => cat.key === key)) {
                const size = calculateObjectSize(data[key]);
                otherSize += size;
                totalSize += size;
            }
        });

        // Ajouter la catégorie "Autres"
        categories.push({ name: 'Autres', size: otherSize, color: '#C7C7C7' });

        // Trier par taille (du plus grand au plus petit)
        categories.sort((a, b) => b.size - a.size);

        return {
            categories,
            totalSize,
            percentOfLimit: (totalSize / 1048576) * 100 // Pourcentage de la limite Firebase
        };
    };

    // Le hook useProfilePicture gère maintenant le chargement de la photo de profil

    // Fonction pour migrer la photo de profil du document utilisateur vers la collection séparée
    const migrateProfilePicture = async (userId, userData) => {
        try {
            // Vérifier si l'utilisateur a une photo de profil dans son document
            if (userData && userData.profilePicture) {
                console.log('Migrating profile picture for user:', userId);
                
                // Vérifier si la photo existe déjà dans la collection profilePictures
                const profilePicRef = firebase.firestore().collection('profilePictures').doc(userId);
                const profilePicDoc = await profilePicRef.get();
                
                if (!profilePicDoc.exists) {
                    // Sauvegarder la photo dans la nouvelle collection
                    await profilePicRef.set({
                        imageData: userData.profilePicture,
                        updatedAt: firebase.firestore.FieldValue.serverTimestamp()
                    });
                    
                    // Supprimer la photo du document utilisateur pour libérer de l'espace
                    const userRef = firebase.firestore().collection('users').doc(userId);
                    await userRef.update({
                        profilePicture: firebase.firestore.FieldValue.delete()
                    });
                    
                    console.log('Profile picture successfully migrated');
                    toast('Your profile data has been optimized');
                }
            }
        } catch (error) {
            console.error('Error migrating profile picture:', error);
        }
    };

    useEffect(() => {
        const checkPlanValidity = async () => {
            if (!user) {
                navigate('/login');
                return;
            }

            try {
                const docRef = firebase.firestore().collection('users').doc(user.uid);
                const docSnapshot = await docRef.get();
                
                if (docSnapshot.exists) {
                    const data = docSnapshot.data();
                    const endSubscriptionDate = data.endSubscriptionDate?.toDate();
                    setNightPerson(data.nightPerson);
                    setUserData(data);
                    setSocialLink(data.socialLink || { type: '', url: '' });
                    setDataSharingConsent(data.dataSharingConsent || false);
                    
                    // Migrer la photo de profil si nécessaire
                    await migrateProfilePicture(user.uid, data);
                    
                    // La photo de profil est chargée automatiquement par le hook useProfilePicture
                    
                    // Charger les paramètres du timer s'ils existent
                    if (data.timerSettings) {
                        setTimerSettings(data.timerSettings);
                    }

                    // Analyser la taille du document
                    const sizeData = analyzeDocumentSize(data);
                    setDocumentSizeData(sizeData);

                    if (endSubscriptionDate > new Date()) {
                        setPlanValidity(2);
                        if (!data.tutorialCompleted) {
                            navigate('/welcome');
                        }
                    } else {
                        setPlanValidity(1);
                    }
                }
            } catch (error) {
                console.error('Error fetching user data:', error);
                toast('Error loading account data');
            }
        };

        checkPlanValidity();
    }, [user, navigate]);

    const formatDate = (timestamp) => {
        if (timestamp?.seconds) {
            const date = new Date(timestamp.seconds * 1000);
            return date.toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
            });
        }
        return 'Not available';
    };

    const handleForgotPassword = async () => {
        try {
            await firebase.auth().sendPasswordResetEmail(userData.email);
            toast('Password reset email sent. Please check your inbox.');
        } catch (error) {
            toast('Error sending password reset email');
        }
    };

    const handleNightPersonChange = async () => {
        try {
            if (user) {
                const docRef = firebase.firestore().collection('users').doc(user.uid);
                await docRef.update({ nightPerson: !nightPerson });
                setNightPerson(!nightPerson);
                toast('Night person preference updated');
            }
        } catch (error) {
            console.error('Error updating night person preference:', error);
            toast('Error updating preference');
        }
    };

    const handleProfilePictureChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Vérifier la taille (max 500KB)
            if (file.size > 500 * 1024) {
                toast('Profile picture must be less than 500KB');
                return;
            }

            // Vérifier le format
            if (!file.type.match(/^image\/(jpeg|jpg|png)$/)) {
                toast('Format accepted: JPG, JPEG, PNG only');
                return;
            }

            const reader = new FileReader();
            reader.onload = (e) => {
                // Utiliser la fonction du hook pour mettre à jour la photo
                updateProfilePicture(e.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSocialLinkChange = (field, value) => {
        setSocialLink(prev => ({ ...prev, [field]: value }));
    };

    const handleDataSharingConsentChange = async () => {
        try {
            if (user) {
                const docRef = firebase.firestore().collection('users').doc(user.uid);
                await docRef.update({ dataSharingConsent: !dataSharingConsent });
                setDataSharingConsent(!dataSharingConsent);
                toast(!dataSharingConsent ? 'Data sharing consent enabled' : 'Data sharing consent disabled');
            }
        } catch (error) {
            console.error('Error updating data sharing consent:', error);
            toast('Error updating consent');
        }
    };

    const validateSocialLink = (type, url) => {
        if (!url) return true; // Lien optionnel
        
        const patterns = {
            whatsapp: /^https:\/\/(wa\.me\/|api\.whatsapp\.com\/send\?phone=)/,
            instagram: /^https:\/\/(www\.)?instagram\.com\/[\w.]+\/?$/,
            linkedin: /^https:\/\/(www\.)?linkedin\.com\/(in|company)\/[\w-]+\/?$/,
            twitter: /^https:\/\/(www\.)?twitter\.com\/[A-Za-z0-9_]{1,15}\/?$/,
            facebook: /^https:\/\/(www\.)?facebook\.com\/[A-Za-z0-9\.]+\/?$/
        };
        
        return patterns[type]?.test(url) || false;
    };

    const handleUpdateProfile = async () => {
        if (socialLink.type && socialLink.url && !validateSocialLink(socialLink.type, socialLink.url)) {
            toast('Invalid link format for selected social network');
            return;
        }

        setIsUpdatingProfile(true);
        try {
            if (user) {
                // Mise à jour des données utilisateur (sans la photo de profil)
                const docRef = firebase.firestore().collection('users').doc(user.uid);
                const updateData = {};
                
                if (socialLink.type && socialLink.url) {
                    updateData.socialLink = socialLink;
                } else {
                    updateData.socialLink = firebase.firestore.FieldValue.delete();
                }
                
                // Supprimer l'ancienne photo de profil du document utilisateur si elle existe
                if (userData && userData.profilePicture) {
                    updateData.profilePicture = firebase.firestore.FieldValue.delete();
                }
                
                await docRef.update(updateData);
                
                // La mise à jour de la photo de profil est déjà gérée par le hook useProfilePicture
                
                // Sync the public testimonial (if exists) with latest profile data
                try {
                    const reviewsRef = firebase.firestore().collection('publicReviews');
                    const snap = await reviewsRef.where('userId', '==', user.uid).limit(1).get();
                    if (!snap.empty) {
                        const reviewId = snap.docs[0].id;
                        await reviewsRef.doc(reviewId).update({
                            profilePicture: profilePicture || '',
                            userName: (userData?.userName || userData?.username || user?.email || '').toString(),
                            ...(socialLink && socialLink.url
                                ? { socialLink }
                                : { socialLink: firebase.firestore.FieldValue.delete() })
                        });
                    }
                } catch (e) {
                    // Non bloquant: si l'update de l'avis échoue, on ignore
                    console.warn('Could not sync publicReviews with latest profile', e);
                }
                toast('Profile updated successfully!');
            }
        } catch (error) {
            console.error('Error updating profile:', error);
            toast('Error updating profile');
        } finally {
            setIsUpdatingProfile(false);
        }
    };

    const handleResetData = async () => {
        try {
            const newData = {
                Goals_1: {
                    experience_1: '',
                    globalView_1: '',
                    intentions_1: '',
                    in10x_1: '',
                    globalView_2: '',
                    intentions_2: '',
                    globalView_3: '',
                    intentions_3: '',
                    processGoals_4: '',
                    values_5: '',
                },
                SingleTasking_2: {
                    priorities_action_list: { columns: [
                        { id: 1, title: 'TO-DO', cards: [] },
                        { id: 2, title: 'DOING', cards: [] },
                        { id: 3, title: 'DONE', cards: [] }
                    ]},
                    secondary_action_list: { columns: [
                        { id: 1, title: 'TO-DO', cards: [] },
                        { id: 2, title: 'DOING', cards: [] },
                        { id: 3, title: 'DONE', cards: [] }
                    ]},
                    easiest_action_list: { columns: [
                        { id: 1, title: 'TO-DO', cards: [] },
                        { id: 2, title: 'DOING', cards: [] },
                        { id: 3, title: 'DONE', cards: [] }
                    ]},
                    clustering_action_list: { columns: [
                        { id: 1, title: 'TO-DO', cards: [] },
                        { id: 2, title: 'DOING', cards: [] },
                        { id: 3, title: 'DONE', cards: [] }
                    ]},
                    delegating_action_list: { columns: [
                        { id: 1, title: 'TO-DO', cards: [] },
                        { id: 2, title: 'DOING', cards: [] },
                        { id: 3, title: 'DONE', cards: [] }
                    ]}
                },
                FlowTrigger_3: {
                    curiosity_1: '',
                    concentration_2: '',
                    skill_3: '',
                    feedback_4: '',
                    richEnv_5: '',
                    deepEnv_6: '',
                    highEnv_7: '',
                    creativity_8: '',
                    triggers_9: ''
                },
                FlowRoutines_4: [{}, {}, {}, {}, {}],
                Release_5: {
                    release_1: '',
                    release_2: '',
                    recoveryEnd_3: '',
                    restOff_4: '',
                    restVacation_5: ''
                },
                DistractionsInterruptions_6: [{}, {}, {}, {}, {}],
                StickingPlan_7: [{}, {}, {}, {}, {}],
                Logistics_8: [],
                TimeManagement_9: [],
                CausesSymptoms_10: [{}, {}, {}, {}, {}],
                FlowTrackerData: []
            };

            await firebase.firestore().collection('users').doc(user.uid).update(newData);
            setEraseDataModal(false);
            toast('Data reset successfully');
            window.location.reload();
        } catch (error) {
            console.error("Error resetting data:", error);
            toast('Error resetting data');
        }
    };

    if (planValidity === 1) {
        return <NoSubscription />;
    }

    return (
        <div className="account-page">
            <DashBoardNavBar userName={userData?.username} userProfilePicture={profilePicture} />
            <div className="account-container">
                <div className="account-header">
                    <h1 className="account-title">Account Settings</h1>
                    <p className="account-subtitle">Manage your account preferences and settings</p>
                </div>

                <div className="account-grid">
                    {/* {documentSizeData && (
                        <div className="account-section document-size-section">
                            <h2 className="section-title">
                                <FiSettings /> Document Size Usage
                            </h2>
                            <div className="document-size-container">
                                <div className="document-size-chart">
                                    <Pie 
                                        data={{
                                            labels: documentSizeData.categories.map(cat => cat.name),
                                            datasets: [{
                                                data: documentSizeData.categories.map(cat => cat.size),
                                                backgroundColor: documentSizeData.categories.map(cat => cat.color),
                                                borderWidth: 1
                                            }]
                                        }}
                                        options={{
                                            plugins: {
                                                legend: {
                                                    position: 'right',
                                                    labels: {
                                                        font: {
                                                            size: 12
                                                        }
                                                    }
                                                },
                                                tooltip: {
                                                    callbacks: {
                                                        label: (context) => {
                                                            const value = context.raw;
                                                            const total = context.dataset.data.reduce((a, b) => a + b, 0);
                                                            const percentage = ((value / total) * 100).toFixed(1);
                                                            const sizeInKB = (value / 1024).toFixed(1);
                                                            return `${context.label}: ${sizeInKB} KB (${percentage}%)`;
                                                        }
                                                    }
                                                }
                                            },
                                            maintainAspectRatio: false
                                        }}
                                        height={300}
                                    />
                                </div>
                                <div className="document-size-info">
                                    <div className="size-info-item total-size">
                                        <div className="info-label">Total Document Size</div>
                                        <div className="info-value">{(documentSizeData.totalSize / 1024).toFixed(1)} KB / 1024 KB</div>
                                        <div className="progress-bar">
                                            <div 
                                                className="progress-fill" 
                                                style={{ 
                                                    width: `${documentSizeData.percentOfLimit}%`,
                                                    backgroundColor: documentSizeData.percentOfLimit > 95 ? '#FF6384' : '#36A2EB'
                                                }}
                                            ></div>
                                        </div>
                                        <div className="size-warning">
                                            {documentSizeData.percentOfLimit > 95 && (
                                                <p className="warning-text">⚠️ Your document is approaching the Firebase size limit (1MB). Consider optimizing your data.</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="size-details">
                                        <h4>Size Details</h4>
                                        <ul className="size-list">
                                            {documentSizeData.categories.slice(0, 5).map((category, index) => (
                                                <li key={index} className="size-list-item">
                                                    <span className="color-dot" style={{ backgroundColor: category.color }}></span>
                                                    <span className="category-name">{category.name}</span>
                                                    <span className="category-size">{(category.size / 1024).toFixed(1)} KB</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )} */}
                    <div className="account-section">
                        <h2 className="section-title">
                            <FiUser /> Profil
                        </h2>
                        <div className="profile-section">
                            <div className="profile-picture-container">
                                <div className="profile-picture-preview">
                                    {profilePicture ? (
                                        <img src={profilePicture} alt="Photo de profil" className="profile-picture" />
                                    ) : (
                                        <div className="profile-picture-placeholder">
                                            <FiCamera size={40} />
                                            <span>No profile picture</span>
                                        </div>
                                    )}
                                </div>
                                <div className="profile-picture-actions">
                                    <input
                                        type="file"
                                        id="profilePicture"
                                        accept="image/jpeg,image/jpg,image/png"
                                        onChange={handleProfilePictureChange}
                                        style={{ display: 'none' }}
                                    />
                                    <label htmlFor="profilePicture" className="action-button primary-button">
                                        <FiCamera /> Choose a photo
                                    </label>
                                    {profilePicture && (
                                        <button 
                                            className="action-button secondary-button"
                                            onClick={() => updateProfilePicture('')}
                                        >
                                            Delete
                                        </button>
                                    )}
                                </div>
                            </div>
                            
                            <div className="social-link-container">
                                <h3><FiLink /> Social Link</h3>
                                <div className="social-link-inputs">
                                    <select 
                                        value={socialLink.type} 
                                        onChange={(e) => handleSocialLinkChange('type', e.target.value)}
                                        className="social-type-select"
                                    >
                                        <option value="">Choisir un réseau</option>
                                        <option value="whatsapp">WhatsApp</option>
                                        <option value="instagram">Instagram</option>
                                        <option value="linkedin">LinkedIn</option>
                                        <option value="twitter">Twitter</option>
                                        <option value="facebook">Facebook</option>
                                    </select>
                                    <input
                                        type="url"
                                        placeholder="https://..."
                                        value={socialLink.url}
                                        onChange={(e) => handleSocialLinkChange('url', e.target.value)}
                                        className="social-url-input"
                                        disabled={!socialLink.type}
                                    />
                                </div>
                                <div className="social-link-help">
                                    {socialLink.type === 'whatsapp' && (
                                        <small>Format: https://wa.me/33123456789</small>
                                    )}
                                    {socialLink.type === 'instagram' && (
                                        <small>Format: https://instagram.com/votre_nom</small>
                                    )}
                                    {socialLink.type === 'linkedin' && (
                                        <small>Format: https://linkedin.com/in/votre-profil</small>
                                    )}
                                    {socialLink.type === 'twitter' && (
                                        <small>Format: https://twitter.com/votre_nom</small>
                                    )}
                                    {socialLink.type === 'facebook' && (
                                        <small>Format: https://facebook.com/votre.profil</small>
                                    )}
                                </div>
                            </div>
                            
                            <button 
                                className="action-button primary-button"
                                onClick={handleUpdateProfile}
                                disabled={isUpdatingProfile}
                            >
                                {isUpdatingProfile ? 'Updating...' : 'Update Profile'}
                            </button>
                        </div>
                    </div>

                    <div className="account-section">
                        <h2 className="section-title">
                            <FiUser /> Profile Information
                        </h2>
                        <div className="info-grid">
                            <div className="info-item">
                                <div className="info-label">User ID</div>
                                <div className="info-value">{user?.uid}</div>
                            </div>
                            <div className="info-item">
                                <div className="info-label">Username</div>
                                <div className="info-value">{userData?.username || 'Not set'}</div>
                            </div>
                            <div className="info-item">
                                <div className="info-label"><FiMail /> Email</div>
                                <div className="info-value">{userData?.email}</div>
                            </div>
                            <div className="info-item">
                                <div className="info-label"><FiCalendar /> Account Created</div>
                                <div className="info-value">{formatDate(userData?.accountCreationDate)}</div>
                            </div>
                            <div className="info-item highlight">
                                <div className="info-label"><FiClock /> Subscription Expires</div>
                                <div className="info-value accent">{formatDate(userData?.endSubscriptionDate)}</div>
                            </div>
                        </div>
                    </div>

                    
                    <div className="account-section">
                        <h2 className="section-title">
                            <FiMoon /> Night Mode Preference
                        </h2>
                        <div className="preference-item">
                            <div className="preference-content">
                                <div className="preference-title">Night Person Mode</div>
                                <div className="preference-description">
                                    Enable for accurate time tracking of after-midnight workflows. 
                                    This helps calculate correct durations for tasks spanning across midnight 
                                    (e.g., 23:00 to 02:00 = 3 hours).
                                </div>
                            </div>
                            <label className="toggle-switch">
                                <input
                                    type="checkbox"
                                    checked={nightPerson}
                                    onChange={handleNightPersonChange}
                                />
                                <span className="toggle-slider"></span>
                            </label>
                        </div>
                    </div>
                    
                    <div className="account-section">
                        <h2 className="section-title">
                            <FiSettings /> Timer Settings
                        </h2>
                        <div className="timer-settings-container">
                            <div className="timer-setting-item">
                                <div className="timer-setting-header">
                                    <div className="timer-setting-title">Focus Timer</div>
                                    <div className="timer-setting-value">{timerSettings.focus} minutes</div>
                                </div>
                                <Slider 
                                    value={timerSettings.focus} 
                                    onChange={(e) => setTimerSettings({...timerSettings, focus: e.value})} 
                                    min={timerLimits.focus.min} 
                                    max={timerLimits.focus.max}
                                    className="timer-slider"
                                />
                                <div className="timer-range-labels">
                                    <span>{timerLimits.focus.min} min</span>
                                    <span>{timerLimits.focus.max} min</span>
                                </div>
                            </div>
                            
                            <div className="timer-setting-item">
                                <div className="timer-setting-header">
                                    <div className="timer-setting-title">Short Break</div>
                                    <div className="timer-setting-value">{timerSettings.shortBreak} minutes</div>
                                </div>
                                <Slider 
                                    value={timerSettings.shortBreak} 
                                    onChange={(e) => setTimerSettings({...timerSettings, shortBreak: e.value})} 
                                    min={timerLimits.shortBreak.min} 
                                    max={timerLimits.shortBreak.max}
                                    className="timer-slider"
                                />
                                <div className="timer-range-labels">
                                    <span>{timerLimits.shortBreak.min} min</span>
                                    <span>{timerLimits.shortBreak.max} min</span>
                                </div>
                            </div>
                            
                            <div className="timer-setting-item">
                                <div className="timer-setting-header">
                                    <div className="timer-setting-title">Long Break</div>
                                    <div className="timer-setting-value">{timerSettings.longBreak} minutes</div>
                                </div>
                                <Slider 
                                    value={timerSettings.longBreak} 
                                    onChange={(e) => setTimerSettings({...timerSettings, longBreak: e.value})} 
                                    min={timerLimits.longBreak.min} 
                                    max={timerLimits.longBreak.max}
                                    className="timer-slider"
                                />
                                <div className="timer-range-labels">
                                    <span>{timerLimits.longBreak.min} min</span>
                                    <span>{timerLimits.longBreak.max} min</span>
                                </div>
                            </div>
                            
                            <button 
                                className="action-button primary-button"
                                onClick={async () => {
                                    setIsUpdatingTimerSettings(true);
                                    try {
                                        if (user) {
                                            const docRef = firebase.firestore().collection('users').doc(user.uid);
                                            await docRef.update({ timerSettings });
                                            toast('Timer settings updated successfully');
                                        }
                                    } catch (error) {
                                        console.error('Error updating timer settings:', error);
                                        toast('Error updating timer settings');
                                    } finally {
                                        setIsUpdatingTimerSettings(false);
                                    }
                                }}
                                disabled={isUpdatingTimerSettings}
                            >
                                {isUpdatingTimerSettings ? 'Updating...' : 'Save Timer Settings'}
                            </button>
                        </div>
                    </div>

                    <div className="account-section">
                        <h2 className="section-title">
                            <FiLock /> Security
                        </h2>
                        <div className="security-item">
                            <div className="security-content">
                                <div className="security-title">Password</div>
                                <div className="security-value">••••••••</div>
                            </div>
                            <button className="action-button primary-button" onClick={handleForgotPassword}>
                                Reset Password
                            </button>
                        </div>
                    </div>

                    <div className="account-section">
                        <h2 className="section-title">
                            <FiLock /> Privacy & Data Sharing
                        </h2>
                        <div className="privacy-content">
                            <div className="privacy-description">
                                <strong>Data Sharing Consent:</strong> By enabling this option, you give the platform administrator access to your data for support purposes and statistical analysis. Your data will only be used for improving the platform and will never be shared with third parties without your explicit consent.
                            </div>
                            <div className="privacy-toggle">
                                <label className="switch">
                                    <input
                                        type="checkbox"
                                        checked={dataSharingConsent}
                                        onChange={handleDataSharingConsentChange}
                                    />
                                    <span className="slider round"></span>
                                </label>
                                <span className="privacy-status">
                                    {dataSharingConsent ? 'Data sharing enabled' : 'Data sharing disabled'}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="account-section danger-zone">
                        <h2 className="section-title">
                            <FiTrash2 /> Data Management
                        </h2>
                        <div className="danger-content">
                            <div className="danger-description">
                                <strong>Warning:</strong> Resetting your data will permanently erase all your:
                                <ul>
                                    <li>Flow state records</li>
                                    <li>Task management data</li>
                                    <li>Personal preferences</li>
                                    <li>Flow tracker history</li>
                                </ul>
                                This action cannot be undone.
                            </div>
                            <div className="data-management-actions">
                                <button className="action-button danger-button" onClick={() => setEraseDataModal(true)}>
                                    Reset All Data
                                </button>
                                
                                <div className="data-optimization-section">
                                    <h3>Data Optimization</h3>
                                    <p>Clean up your FlowTracker data by removing empty fields to reduce document size.</p>
                                    <button 
                                        className="action-button primary-button" 
                                        onClick={async () => {
                                            if (!user) return;
                                            
                                            setIsCleaningFlowTracker(true);
                                            try {
                                                const stats = await flowTrackerCleanupService.cleanupFlowTrackerData(user.uid);
                                                setCleanupStats(stats);
                                                toast('FlowTracker data cleaned successfully');
                                                
                                                // Refresh document size data
                                                const docRef = firebase.firestore().collection('users').doc(user.uid);
                                                const docSnapshot = await docRef.get();
                                                if (docSnapshot.exists) {
                                                    const data = docSnapshot.data();
                                                    const sizeData = analyzeDocumentSize(data);
                                                    setDocumentSizeData(sizeData);
                                                }
                                            } catch (error) {
                                                console.error('Error cleaning FlowTracker data:', error);
                                                toast('Error cleaning FlowTracker data');
                                            } finally {
                                                setIsCleaningFlowTracker(false);
                                            }
                                        }}
                                        disabled={isCleaningFlowTracker}
                                    >
                                        {isCleaningFlowTracker ? 'Cleaning...' : 'Clean FlowTracker Data'}
                                    </button>
                                    
                                    {cleanupStats && (
                                        <div className="cleanup-stats">
                                            <h4>Cleanup Results</h4>
                                            <p><strong>Space Reduced:</strong> {(cleanupStats.spaceReduced / 1024).toFixed(2)} KB ({cleanupStats.percentReduced}%)</p>
                                            <p><strong>Entries Cleaned:</strong> {cleanupStats.entriesCleaned}</p>
                                        </div>
                                    )}
                                </div>

                                <div className="data-optimization-section">
                                    <h3>FlowTracker Data Migration</h3>
                                    <p>Move your legacy FlowTracker history to the new optimized collection (flowTrackerData).</p>
                                    <button
                                        className="action-button primary-button"
                                        onClick={async () => {
                                            if (!user) return;
                                            
                                            const deleteOldData = window.confirm(
                                                'Do you want to delete the legacy FlowTrackerData from your user document after migration?\n\nThis will reduce document size but cannot be undone.'
                                            );

                                            setIsMigratingFlowTracker(true);
                                            try {
                                                const stats = await flowTrackerService.migrateExistingData(user.uid, { deleteOldData });
                                                setMigrationStats(stats);
                                                toast('FlowTracker migration completed');

                                                // Refresh document size data
                                                const docRef = firebase.firestore().collection('users').doc(user.uid);
                                                const docSnapshot = await docRef.get();
                                                if (docSnapshot.exists) {
                                                    const data = docSnapshot.data();
                                                    const sizeData = analyzeDocumentSize(data);
                                                    setDocumentSizeData(sizeData);
                                                }
                                            } catch (error) {
                                                console.error('Error migrating FlowTracker data:', error);
                                                toast('Error migrating FlowTracker data');
                                            } finally {
                                                setIsMigratingFlowTracker(false);
                                            }
                                        }}
                                        disabled={isMigratingFlowTracker}
                                    >
                                        {isMigratingFlowTracker ? 'Migrating...' : 'Migrate FlowTracker Data'}
                                    </button>

                                    {migrationStats && (
                                        <div className="cleanup-stats">
                                            <h4>Migration Results</h4>
                                            <p><strong>Legacy Entries:</strong> {migrationStats.totalLegacyEntries}</p>
                                            <p><strong>Migrated:</strong> {migrationStats.migratedEntries}</p>
                                            <p><strong>Skipped (already existed/invalid):</strong> {migrationStats.skippedEntries}</p>
                                            <p><strong>Deleted Legacy Data:</strong> {migrationStats.deletedLegacyData ? 'Yes' : 'No'}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {eraseDataModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h2 className="modal-title">Confirm Data Reset</h2>
                            <button
                                className="modal-close"
                                onClick={() => setEraseDataModal(false)}
                                aria-label="Close"
                            >
                                ×
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="warning-icon">⚠️</div>
                            <p className="modal-message">
                                Are you absolutely sure you want to reset all your data? 
                                This will permanently erase everything and cannot be undone.
                            </p>
                            <div className="modal-actions">
                                <button 
                                    className="action-button secondary-button" 
                                    onClick={() => setEraseDataModal(false)}
                                >
                                    Cancel
                                </button>
                                <button 
                                    className="action-button danger-button" 
                                    onClick={handleResetData}
                                >
                                    Yes, Reset Everything
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Account;
